<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import KanbanAPI from '../../../../api/kanban';
import { emitter } from 'shared/helpers/mitt';

const store = useStore();

const emit = defineEmits(['close', 'confirm']);

const selectedInboxId = ref(null);
const isLoading = ref(false);
const result = ref(null);

const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);

const selectedFunnel = computed(
  () => store.getters['funnel/getSelectedFunnel']
);

onMounted(() => {
  store.dispatch('inboxes/get');
});

const handleConfirm = async () => {
  if (!selectedInboxId.value) return;

  isLoading.value = true;
  result.value = null;

  try {
    const response = await KanbanAPI.bulkRelinkConversations(
      selectedInboxId.value,
      selectedFunnel.value?.id
    );

    result.value = response.data;

    if (response.data.linked > 0) {
      emitter.emit('newToastMessage', {
        message: `${response.data.linked} conversa(s) vinculada(s) de ${response.data.total} item(ns)`,
        action: { type: 'success' },
      });
      emit('confirm', response.data);
    } else {
      emitter.emit('newToastMessage', {
        message:
          response.data.message ||
          'Nenhuma conversa encontrada para vincular neste canal',
        action: { type: 'warning' },
      });
    }
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: error.response?.data?.error || 'Erro ao vincular conversas',
      action: { type: 'error' },
    });
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <div class="p-6">
    <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
      Vincular Conversas
    </h3>
    <p class="text-sm text-slate-500 dark:text-slate-400 mb-6">
      Busca e vincula automaticamente conversas para todos os itens que possuem
      contato mas não possuem conversa vinculada.
    </p>

    <!-- Inbox selector -->
    <div class="mb-6">
      <label
        class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
      >
        Canal
      </label>
      <select
        v-model="selectedInboxId"
        class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:ring-2 focus:ring-woot-500 focus:border-transparent"
        :disabled="isLoading"
      >
        <option :value="null" disabled>Selecione um canal</option>
        <option
          v-for="inbox in inboxes"
          :key="inbox.id"
          :value="inbox.id"
        >
          {{ inbox.name }}
        </option>
      </select>
    </div>

    <!-- Resultado -->
    <div
      v-if="result"
      class="mb-6 p-4 rounded-lg border"
      :class="
        result.linked > 0
          ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
          : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
      "
    >
      <div class="flex items-center gap-2 mb-2">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          :class="
            result.linked > 0
              ? 'text-green-600 dark:text-green-400'
              : 'text-amber-600 dark:text-amber-400'
          "
        >
          <path v-if="result.linked > 0" d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <path v-if="result.linked > 0" d="m9 11 3 3L22 4" />
          <path v-if="result.linked === 0" d="M12 9v4" />
          <path v-if="result.linked === 0" d="M12 17h.01" />
          <circle v-if="result.linked === 0" cx="12" cy="12" r="10" />
        </svg>
        <span
          class="text-sm font-medium"
          :class="
            result.linked > 0
              ? 'text-green-700 dark:text-green-300'
              : 'text-amber-700 dark:text-amber-300'
          "
        >
          Resultado
        </span>
      </div>
      <div class="text-sm text-slate-700 dark:text-slate-300 space-y-1">
        <div>
          <span class="font-medium">{{ result.linked }}</span> conversa(s)
          vinculada(s)
        </div>
        <div>
          <span class="font-medium">{{ result.skipped }}</span>
          item(ns) sem conversa neste canal
        </div>
        <div class="text-xs text-slate-500 dark:text-slate-400">
          Total de itens processados: {{ result.total }}
        </div>
      </div>
    </div>

    <!-- Ações -->
    <div class="flex items-center justify-end gap-3">
      <button
        type="button"
        class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
        @click="emit('close')"
      >
        {{ result ? 'Fechar' : 'Cancelar' }}
      </button>
      <button
        v-if="!result || result.linked === 0"
        type="button"
        class="px-4 py-2 text-sm font-medium text-white bg-woot-500 hover:bg-woot-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        :disabled="!selectedInboxId || isLoading"
        @click="handleConfirm"
      >
        <svg
          v-if="isLoading"
          class="animate-spin h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          />
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
        {{ isLoading ? 'Processando...' : 'Vincular Conversas' }}
      </button>
    </div>
  </div>
</template>
